import { getIsSizeWithinBounds } from '@lib/size'
import { type SanityFrameBuilderType } from '@frame-builder/data/sanity/queries/types'
import { type SanityFrameBuilderProductPassepartoutColor } from '@frame-builder/data/sanity/queries/types/passepartout'
import { type SanityFrameBuilderProductProfile } from '@frame-builder/data/sanity/queries/types/profile'
import { SanityFrameBuilderProductProfileTexture } from '@frame-builder/data/sanity/queries/types/profile-texture'
import { type SanityFrameBuilderProductSpacerSize } from '@frame-builder/data/sanity/queries/types/spacer'
import { type SanityFrameBuilderProductTexture } from '@frame-builder/data/sanity/queries/types/texture'
import { arrayToSize, getProductFrameSize } from '@frame-builder/lib/size'

/**
 * Determines if frame size fits within profile's maximum dimensions.
 */
export const getIsProductProfileSizeValid = (
  profile: SanityFrameBuilderProductProfile,
  artworkWidth?: number,
  artworkHeight?: number,
  passepartoutColor?: SanityFrameBuilderProductPassepartoutColor,
  passepartoutWidth?: number,
  passepartoutHeight?: number,
) => {
  if (
    typeof artworkWidth === 'undefined' ||
    typeof artworkHeight === 'undefined'
  ) {
    return true
  }

  const frameSize = getProductFrameSize({
    artwork: {
      width: artworkWidth,
      height: artworkHeight,
    },
    passepartout: {
      color: passepartoutColor,
      width: passepartoutWidth,
      height: passepartoutHeight,
    },
  })
  const isProductProfileSizeValid = getIsSizeWithinBounds(
    frameSize,
    arrayToSize([profile.minimumSize, profile.minimumSize]),
    arrayToSize([profile.maximumWidth, profile.maximumHeight]),
  )

  return isProductProfileSizeValid
}

/**
 * Determines if mount color is valid.
 */
export const getIsProductPassepartoutColorValid = (
  frameBuilderType: SanityFrameBuilderType,
  passepartoutColor?: SanityFrameBuilderProductPassepartoutColor,
) => {
  if (frameBuilderType === 'passepartout') {
    return typeof passepartoutColor !== 'undefined'
  }

  return true
}

/**
 * Determines if mount size fits within mount color's maximum dimensions.
 */
export const getIsProductPassepartoutSizeValid = (
  artworkWidth?: number,
  artworkHeight?: number,
  passepartoutColor?: SanityFrameBuilderProductPassepartoutColor,
  passepartoutWidth?: number,
  passepartoutHeight?: number,
) => {
  if (
    typeof artworkWidth === 'undefined' ||
    typeof artworkHeight === 'undefined' ||
    typeof passepartoutColor === 'undefined'
  ) {
    return true
  }

  if (
    typeof passepartoutWidth === 'undefined' ||
    typeof passepartoutHeight === 'undefined'
  ) {
    return false
  }

  // Get frame size (which is equal to mount size)
  const frameSize = getProductFrameSize({
    artwork: {
      width: artworkWidth,
      height: artworkHeight,
    },
    passepartout: {
      color: passepartoutColor,
      width: passepartoutWidth,
      height: passepartoutHeight,
    },
  })

  if (
    typeof passepartoutColor.maximumWidth === 'undefined' ||
    typeof passepartoutColor.maximumHeight === 'undefined'
  ) {
    return true
  }

  // Validate mount size compared to mount maximum size
  return (
    (frameSize[0] <= passepartoutColor.maximumWidth &&
      frameSize[1] <= passepartoutColor.maximumHeight) ||
    (frameSize[0] <= passepartoutColor.maximumHeight &&
      frameSize[1] <= passepartoutColor.maximumWidth)
  )
}

/**
 * Determines if profile and texture have been connected in a profile.
 */
export const getIsProductProfileAvailableForTexture = (
  profile: SanityFrameBuilderProductProfile,
  texture: SanityFrameBuilderProductTexture,
  profileTextures: SanityFrameBuilderProductProfileTexture[],
) => {
  const hasProfile = profileTextures.some((profileTexture) => {
    return (
      profileTexture.profile?._ref === profile._id &&
      profileTexture.texture?._ref === texture._id
    )
  })

  return hasProfile
}

/**
 * Determines if profile is available for mount color.
 */
export const getIsProductProfileAvailableForPassepartoutColor = (
  profile: SanityFrameBuilderProductProfile,
  passepartoutColor: SanityFrameBuilderProductPassepartoutColor,
) => {
  const isAvailableForProfile = !!passepartoutColor.mouldingIds?.some(
    (mouldingId) => mouldingId === profile._id,
  )

  return isAvailableForProfile
}

/**
 * Determines if profile is available for spacer size.
 */
export const getIsProductProfileAvailableForSpacerSize = (
  profile: SanityFrameBuilderProductProfile,
  spacerSize: SanityFrameBuilderProductSpacerSize,
) => {
  const isAvailableForProfile = !!spacerSize.mouldingIds?.some(
    (mouldingId) => mouldingId === profile._id,
  )

  return isAvailableForProfile
}
